import {ButtonGroup} from '@geomatico/geocomponents';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import TocOutlinedIcon from '@mui/icons-material/TocOutlined';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type {GridFilterModel, GridSortModel} from '@mui/x-data-grid';
import {type FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Title from '@/components/common/Title';
import ForestMassesMap from '@/components/ForestMassList/ForestMassesMap';
import ForestMassesTable from '@/components/ForestMassList/ForestMassesTable';
import {VIEW_TYPE_ID} from '@/config';
import type {ForestMassStatus} from '@/domain/entities/ForestMassStatus';
import type UserPermissions from '@/domain/useCases/userPermissions';

//TYPES
type Props = {
  forestMassesStatuses?: Array<ForestMassStatus>;
  permissions: UserPermissions;
  onAdd: () => void;
  onForestMassClick: (uuid: string) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
};

const ForestMassesList: FC<Props> = ({
  forestMassesStatuses,
  permissions,
  onAdd,
  onForestMassClick,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
}) => {
  const [viewType, setViewType] = useState('table');
  const {t} = useTranslation();

  const FORESTMASS_LIST_VIEW_TYPE = [
    {
      id: VIEW_TYPE_ID.TABLE,
      content: (
        <Stack direction="row" spacing={1}>
          <TocOutlinedIcon />
          <Typography sx={styles.buttonText}>{t('forestMass.list')}</Typography>
        </Stack>
      ),
    },
    {
      id: VIEW_TYPE_ID.MAP,
      content: (
        <Stack direction="row" spacing={1}>
          <MapOutlinedIcon />
          <Typography sx={styles.buttonText}>{t('forestMass.map')}</Typography>
        </Stack>
      ),
    },
  ];

  if (!forestMassesStatuses) return;

  return (
    <>
      <Stack direction="row" sx={styles.titleContainer}>
        <Title label={t('forestMass.listTitle')} />
        <ButtonGroup
          items={FORESTMASS_LIST_VIEW_TYPE}
          selectedItemId={viewType}
          variant="outlined"
          onItemClick={setViewType}
          sx={styles.buttonGroup}
        />
      </Stack>

      {viewType === VIEW_TYPE_ID.TABLE ? (
        <ForestMassesTable
          forestMassesStatuses={forestMassesStatuses}
          onForestMassClick={onForestMassClick}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
        />
      ) : (
        <Stack sx={styles.mapContainer}>
          <ForestMassesMap forestMassesStatuses={forestMassesStatuses} onForestMassClick={onForestMassClick} />
        </Stack>
      )}
      <Button
        variant="contained"
        sx={styles.button}
        onClick={onAdd}
        disabled={!permissions.hasPermissionToAddForestMass()}
      >
        {t('forestMass.add')}
      </Button>
    </>
  );
};
export default ForestMassesList;

const styles = {
  titleContainer: {
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1,
  },
  title: {
    color: 'primary.main',
  },
  buttonGroup: {
    '&.ButtonGroup-root': {
      height: '30px',
    },
    '& .ButtonGroup-button': {
      borderRadius: 0,
    },
  },
  buttonText: {
    textTransform: 'capitalize',
  },
  mapContainer: {
    width: '100%',
    height: '100%',
    p: 2,
    bgcolor: 'common.white',
  },
  button: {
    my: '1rem',
    alignSelf: 'end',
    width: '250px',
  },
};
